import isEqual from 'lodash.isequal';
import * as React from 'react';
import { noop } from 'vet-bones/utils';

import { dataSourcesStardogRequestDispatchers } from 'src/actions/data-sources/actionCreators';
import { Resource } from 'src/containers/ProjectDiagram/types/data';
import { useMappingFromRoute } from 'src/containers/ProjectMapping/hooks/useMappingFromRoute';
import { connectionSelector } from 'src/selectors/connection';
import { isConnectedSelector } from 'src/selectors/isConnected';
import { isConnectingSelector } from 'src/selectors/isConnecting';
import { projectNodeDataSelector } from 'src/selectors/projectNodeData';
import { projectVirtualGraphSelector } from 'src/selectors/projectVirtualGraph';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { usePortalConnection } from 'src/utils/hooks/usePortalConnection';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

type SuggestionsContextType = {
  isLoading: boolean;
  loadingMappingId: string;
  setLoadingMappingId: (loadingMappingId: string) => void;
};

export const SuggestionsContext = React.createContext<SuggestionsContextType>({
  isLoading: false,
  loadingMappingId: '',
  setLoadingMappingId: noop,
});

export const useSuggestionContext = () => {
  return React.useContext(SuggestionsContext);
};

export const useSuggestionContextValue = (): SuggestionsContextType => {
  const { mappingId, projectId } = useRouterParams<'PROJECT_MAPPING'>();
  const mapping = useMappingFromRoute();

  const [loadingMappingId, setLoadingMappingId] = React.useState('');

  const isMounted = React.useRef(false);
  const didMappingIdChange = useDidValueChange(mappingId);
  React.useEffect(() => {
    // do not reset the state on the initial mount
    if (
      isMounted.current &&
      didMappingIdChange &&
      mappingId !== loadingMappingId
    ) {
      setLoadingMappingId('');
    }
  }, [didMappingIdChange, isMounted, loadingMappingId, mappingId]);
  // this effect must happen after the effect to reset state above
  React.useEffect(() => {
    isMounted.current = true;
  }, []);

  const resourceData = useReduxSelector(
    projectNodeDataSelector(projectId, mapping?.from),
    isEqual
  ) as Resource | null;

  const virtualGraph = useReduxSelector(
    projectVirtualGraphSelector(projectId, resourceData?.value)
  );

  const connection = useReduxSelector(connectionSelector);
  const isConnected = useReduxSelector(isConnectedSelector);
  const isConnecting = useReduxSelector(isConnectingSelector);

  const didIsConnectingChange = useDidValueChange(isConnecting);

  usePortalConnection({
    connectionId: virtualGraph?.connectionId,
    enabled: mappingId !== undefined,
    key: mappingId,
  });

  React.useEffect(() => {
    if (!isConnecting && didIsConnectingChange && isConnected && connection) {
      dataSourcesStardogRequestDispatchers.requestDataSourceTypeDescription(
        connection
      );
    }
  }, [connection, didIsConnectingChange, isConnected, isConnecting]);

  return {
    isLoading: Boolean(loadingMappingId),
    loadingMappingId,
    setLoadingMappingId,
  };
};
